/**
 * Student Medical Record — comprehensive, aesthetic modal.
 * Reads the full clinical graph for a student and lays it out as a
 * single-page passport: hero, vitals, alerts, timeline, and archives.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  Activity, AlertTriangle, HeartPulse, Pill, Syringe, Stethoscope, Brain,
  FileText, Shield, Phone, User, Droplet, Ruler, Weight, ClipboardList,
  Sparkles, CalendarDays, Baby, ScanLine, Download, Printer, ArrowUpRight,
} from "lucide-react";
import { shortDateTime } from "@/lib/format";

type Props = { studentId: string; onClose: () => void };

export function StudentMedicalRecordModal({ studentId, onClose }: Props) {
  const { data: student } = useQuery({
    queryKey: ["mr-student", studentId],
    queryFn: async () => (await supabase.from("students").select("*").eq("id", studentId).maybeSingle()).data,
  });

  const { data: record } = useQuery({
    queryKey: ["mr-record", studentId],
    queryFn: async () => (await supabase.from("health_records").select("*").eq("student_id", studentId).maybeSingle()).data,
  });

  const { data: allergies = [] } = useQuery({
    queryKey: ["mr-allergies", studentId],
    queryFn: async () => (await supabase.from("health_allergies").select("*").eq("student_id", studentId).order("created_at", { ascending: false })).data ?? [],
  });

  const { data: conditions = [] } = useQuery({
    queryKey: ["mr-conditions", studentId],
    queryFn: async () => (await supabase.from("health_conditions").select("*").eq("student_id", studentId).order("diagnosed_date", { ascending: false })).data ?? [],
  });

  const { data: medications = [] } = useQuery({
    queryKey: ["mr-medications", studentId],
    queryFn: async () => (await supabase.from("health_medications").select("*").eq("student_id", studentId).order("start_date", { ascending: false })).data ?? [],
  });

  const { data: immunizations = [] } = useQuery({
    queryKey: ["mr-immunizations", studentId],
    queryFn: async () => (await supabase.from("health_immunizations").select("*").eq("student_id", studentId).order("administered_date", { ascending: false })).data ?? [],
  });

  const { data: episodes = [] } = useQuery({
    queryKey: ["mr-episodes", studentId],
    queryFn: async () => (await supabase.from("care_episodes").select("*").eq("student_id", studentId).order("opened_at", { ascending: false }).limit(20)).data ?? [],
  });

  const { data: observations = [] } = useQuery({
    queryKey: ["mr-obs", studentId],
    queryFn: async () => (await supabase.from("care_observations").select("*, care_episodes!inner(student_id)").eq("care_episodes.student_id", studentId).order("recorded_at", { ascending: false }).limit(30)).data ?? [],
  });

  const { data: visits = [] } = useQuery({
    queryKey: ["mr-visits", studentId],
    queryFn: async () => (await supabase.from("health_visits").select("*").eq("student_id", studentId).order("visit_date", { ascending: false }).limit(30)).data ?? [],
  });

  const { data: checkins = [] } = useQuery({
    queryKey: ["mr-mh", studentId],
    queryFn: async () => (await supabase.from("mental_health_checkins").select("*").eq("student_id", studentId).order("submitted_at", { ascending: false }).limit(40)).data ?? [],
  });

  const { data: referrals = [] } = useQuery({
    queryKey: ["mr-refs", studentId],
    queryFn: async () => (await supabase.from("health_referrals").select("*").eq("student_id", studentId).order("created_at", { ascending: false }).limit(20)).data ?? [],
  });

  const { data: docs = [] } = useQuery({
    queryKey: ["mr-docs", studentId],
    queryFn: async () => (await supabase.from("health_documents").select("*").eq("student_id", studentId).order("created_at", { ascending: false })).data ?? [],
  });

  const { data: sen = [] } = useQuery({
    queryKey: ["mr-sen", studentId],
    queryFn: async () => (await supabase.from("sen_accommodations").select("*").eq("student_id", studentId).eq("active", true)).data ?? [],
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["mr-plans", studentId],
    queryFn: async () => (await supabase.from("care_plans").select("*").eq("student_id", studentId).eq("status", "active")).data ?? [],
  });

  const { data: screenings = [] } = useQuery({
    queryKey: ["mr-screen", studentId],
    queryFn: async () => (await supabase.from("health_screening_results").select("*, health_screening_campaigns(campaign_name)").eq("student_id", studentId).order("screened_at", { ascending: false }).limit(20)).data ?? [],
  });

  const bmi = useMemo(() => {
    if (!record?.height_cm || !record?.weight_kg) return null;
    const h = record.height_cm / 100;
    return +(record.weight_kg / (h * h)).toFixed(1);
  }, [record]);

  const moodAvg = checkins.length ? +(checkins.reduce((a: number, c: any) => a + (c.mood_score || 0), 0) / checkins.length).toFixed(1) : null;
  const openEpisodes = episodes.filter((e: any) => e.status === "open").length;

  // ---- Tabs ----
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl p-0 overflow-hidden max-h-[92vh]">
        <div className="grid grid-rows-[auto_1fr] max-h-[92vh]">
          {/* ================= HERO ================= */}
          <div className="relative overflow-hidden text-white"
               style={{ background: `linear-gradient(120deg, #3a1a10 0%, ${student?.color_hex ?? "#8B3A1F"} 55%, #A42022 100%)` }}>
            <div className="absolute inset-0 opacity-[0.12] mix-blend-overlay"
                 style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 70% 60%, #fff 1px, transparent 1px)", backgroundSize: "42px 42px, 26px 26px" }} />
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-[#c9a84c]/25 blur-3xl" />

            <div className="relative p-6 md:p-8">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="relative grid h-20 w-20 place-items-center rounded-2xl font-serif text-2xl font-bold shadow-2xl ring-4 ring-white/25"
                    style={{ background: `linear-gradient(135deg, #fff2, #0002)`, backdropFilter: "blur(6px)" }}>
                    {student?.initials || "·"}
                    <div className="absolute -bottom-2 -right-2 grid h-7 w-7 place-items-center rounded-full border-2 border-white bg-[var(--terracotta,#A42022)] shadow">
                      <HeartPulse className="h-3.5 w-3.5 text-white animate-pulse" />
                    </div>
                  </motion.div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/70">Medical passport · TriSphere Care</div>
                    <h2 className="mt-1 font-serif text-3xl leading-tight">{student?.full_name ?? "—"}</h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-white/80">
                      <span>{student?.class_label ?? "—"}</span>
                      <span className="opacity-40">•</span>
                      <span>{student?.student_no ?? "—"}</span>
                      <span className="opacity-40">•</span>
                      <span>{student?.boarding ? "Boarding" : "Day scholar"}</span>
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex flex-col gap-2 text-right">
                  <Button variant="secondary" size="sm" onClick={() => window.print()}><Printer className="mr-1.5 h-3.5 w-3.5" /> Print</Button>
                  <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20"><Download className="mr-1.5 h-3.5 w-3.5" /> Export PDF</Button>
                </div>
              </div>

              {/* Vitals band */}
              <div className="mt-6 grid grid-cols-2 gap-2 md:grid-cols-6">
                <HeroVital icon={Droplet} label="Blood group" value={record?.blood_group ?? "—"} />
                <HeroVital icon={Ruler} label="Height" value={record?.height_cm ? `${record.height_cm} cm` : "—"} />
                <HeroVital icon={Weight} label="Weight" value={record?.weight_kg ? `${record.weight_kg} kg` : "—"} />
                <HeroVital icon={Activity} label="BMI" value={bmi ? String(bmi) : "—"} tone={bmi ? bmiTone(bmi) : undefined} />
                <HeroVital icon={AlertTriangle} label="Allergies" value={String(allergies.length)} tone={allergies.length ? "warn" : undefined} />
                <HeroVital icon={Stethoscope} label="Open episodes" value={String(openEpisodes)} tone={openEpisodes ? "warn" : undefined} />
              </div>

              {/* Alert strip */}
              {(allergies.length > 0 || conditions.length > 0) && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {allergies.slice(0, 4).map((a: any) => (
                    <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-[#c9a84c]/25 px-2.5 py-1 text-[11px] font-medium ring-1 ring-[#c9a84c]/50">
                      <AlertTriangle className="h-3 w-3" /> {a.allergen} · {a.severity}
                    </span>
                  ))}
                  {conditions.slice(0, 3).map((c: any) => (
                    <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium ring-1 ring-white/30">
                      <Sparkles className="h-3 w-3" /> {c.condition_name}
                    </span>
                  ))}
                  {moodAvg !== null && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] ring-1 ring-white/20">
                      <Brain className="h-3 w-3" /> Mood avg {moodAvg}/10
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ================= BODY ================= */}
          <div className="overflow-auto">
            <Tabs defaultValue="overview" className="p-4 md:p-6">
              <TabsList className="flex flex-wrap gap-1 bg-transparent">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="clinical">Clinical</TabsTrigger>
                <TabsTrigger value="medications">Medications</TabsTrigger>
                <TabsTrigger value="immunizations">Immunizations</TabsTrigger>
                <TabsTrigger value="history">Care history</TabsTrigger>
                <TabsTrigger value="wellbeing">Wellbeing</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
              </TabsList>

              {/* ---------- OVERVIEW ---------- */}
              <TabsContent value="overview" className="mt-4 grid gap-3 lg:grid-cols-3">
                <SectionCard icon={Shield} title="Care summary" tone="terracotta" className="lg:col-span-2">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <MiniStat label="Care episodes" value={episodes.length} sub={`${openEpisodes} open`} />
                    <MiniStat label="Referrals" value={referrals.length} sub={`${referrals.filter((r:any)=>r.status==='open').length} pending`} />
                    <MiniStat label="Active care plans" value={plans.length} sub={plans.map((p:any)=>p.plan_name).join(" · ") || "—"} />
                    <MiniStat label="SEN accommodations" value={sen.length} sub={sen.map((s:any)=>s.category).join(" · ") || "—"} />
                    <MiniStat label="Clinic visits" value={visits.length} sub={visits[0]?.visit_date ? `last ${new Date(visits[0].visit_date).toLocaleDateString()}` : "—"} />
                    <MiniStat label="Screenings" value={screenings.length} sub={screenings[0]?.health_screening_campaigns?.campaign_name || "—"} />
                  </div>
                </SectionCard>

                <SectionCard icon={Phone} title="Emergency contact" tone="ochre">
                  {record?.emergency_contact_name ? (
                    <div className="space-y-1.5 text-sm">
                      <div className="font-semibold text-base">{record.emergency_contact_name}</div>
                      <div className="text-muted-foreground">{record.emergency_contact_relation ?? "Guardian"}</div>
                      <a href={`tel:${record.emergency_contact_phone}`} className="inline-flex items-center gap-1.5 rounded-md bg-[var(--terracotta,#A42022)]/10 px-2.5 py-1.5 text-[var(--terracotta,#A42022)] font-mono">
                        <Phone className="h-3.5 w-3.5" /> {record.emergency_contact_phone}
                      </a>
                      {record.primary_physician && <div className="text-xs pt-2 border-t">Physician · {record.primary_physician}</div>}
                      {record.medical_insurer && <div className="text-xs">Insurer · {record.medical_insurer}</div>}
                    </div>
                  ) : <Empty label="No emergency contact on file" />}
                </SectionCard>

                <SectionCard icon={AlertTriangle} title="Allergies" tone="terracotta">
                  <div className="space-y-1.5">
                    {allergies.map((a:any) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg border border-[var(--terracotta,#A42022)]/30 bg-[var(--terracotta,#A42022)]/5 p-2 text-xs">
                        <div>
                          <div className="font-semibold">{a.allergen}</div>
                          <div className="text-muted-foreground">{a.reaction}</div>
                        </div>
                        <Badge variant={a.severity === "severe" ? "destructive" : "outline"}>{a.severity}</Badge>
                      </div>
                    ))}
                    {allergies.length === 0 && <Empty label="No known allergies" />}
                  </div>
                </SectionCard>

                <SectionCard icon={Sparkles} title="Chronic conditions" tone="forest">
                  <div className="space-y-1.5">
                    {conditions.map((c:any) => (
                      <div key={c.id} className="rounded-lg border p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">{c.condition_name}</div>
                          <Badge variant="outline" className="text-[9px]">{c.category}</Badge>
                        </div>
                        {c.diagnosed_date && <div className="text-muted-foreground">Diagnosed {new Date(c.diagnosed_date).toLocaleDateString()}</div>}
                        {c.notes && <div className="mt-1 text-[11px]">{c.notes}</div>}
                      </div>
                    ))}
                    {conditions.length === 0 && <Empty label="No chronic conditions" />}
                  </div>
                </SectionCard>

                <SectionCard icon={ClipboardList} title="Recent observations" tone="ink">
                  <div className="space-y-1 text-xs">
                    {observations.slice(0, 6).map((o:any) => (
                      <div key={o.id} className="flex items-center justify-between border-b py-1">
                        <div>
                          <div className="font-medium capitalize">{o.observation_type}</div>
                          <div className="text-[10px] text-muted-foreground">{shortDateTime(o.recorded_at)}</div>
                        </div>
                        <div className="font-mono font-semibold text-sm">{o.value_numeric ?? o.value_text ?? "—"} <span className="text-[10px] text-muted-foreground">{o.unit}</span></div>
                      </div>
                    ))}
                    {observations.length === 0 && <Empty label="No observations yet" />}
                  </div>
                </SectionCard>
              </TabsContent>

              {/* ---------- CLINICAL ---------- */}
              <TabsContent value="clinical" className="mt-4 grid gap-3 lg:grid-cols-2">
                <SectionCard icon={AlertTriangle} title="Allergy register" tone="terracotta">
                  <TableList
                    rows={allergies}
                    empty="No allergies"
                    render={(a:any) => (
                      <>
                        <td className="py-2 font-medium">{a.allergen}</td>
                        <td className="text-muted-foreground">{a.reaction}</td>
                        <td><Badge variant={a.severity==='severe'?'destructive':'outline'}>{a.severity}</Badge></td>
                        <td className="text-[10px] text-muted-foreground">{a.verified ? "verified" : "unverified"}</td>
                      </>
                    )}
                    head={["Allergen", "Reaction", "Severity", "Status"]}
                  />
                </SectionCard>

                <SectionCard icon={Sparkles} title="Diagnosed conditions" tone="forest">
                  <TableList
                    rows={conditions}
                    empty="No conditions"
                    render={(c:any) => (
                      <>
                        <td className="py-2 font-medium">{c.condition_name}</td>
                        <td className="text-muted-foreground">{c.category}</td>
                        <td className="text-xs">{c.diagnosed_date ? new Date(c.diagnosed_date).toLocaleDateString() : "—"}</td>
                        <td>{c.active ? <Badge variant="destructive">active</Badge> : <Badge variant="secondary">resolved</Badge>}</td>
                      </>
                    )}
                    head={["Condition", "Category", "Since", "Status"]}
                  />
                </SectionCard>

                <SectionCard icon={ClipboardList} title="Care plans" tone="ochre">
                  <div className="space-y-2">
                    {plans.map((p:any) => (
                      <div key={p.id} className="rounded-lg border p-3">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">{p.plan_name}</div>
                          <Badge variant="outline">Review {p.review_date ? new Date(p.review_date).toLocaleDateString() : "—"}</Badge>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                          <MiniList title="Triggers" items={p.triggers} />
                          <MiniList title="Monitoring" items={p.monitoring} />
                          <MiniList title="Escalation" items={p.escalation} />
                          <MiniList title="Restrictions" items={p.restrictions} />
                        </div>
                      </div>
                    ))}
                    {plans.length === 0 && <Empty label="No active care plans" />}
                  </div>
                </SectionCard>

                <SectionCard icon={Baby} title="SEN & accommodations" tone="ink">
                  <div className="space-y-2">
                    {sen.map((s:any) => (
                      <div key={s.id} className="rounded-lg border p-2.5 text-xs">
                        <div className="flex justify-between font-medium"><span>{s.category}</span>{s.responsible_staff && <span className="text-[10px] text-muted-foreground">{s.responsible_staff}</span>}</div>
                        {s.description && <div className="text-muted-foreground">{s.description}</div>}
                        {Array.isArray(s.accommodations) && <div className="mt-1 flex flex-wrap gap-1">{s.accommodations.map((x:string,i:number)=><Badge key={i} variant="outline" className="text-[9px]">{x}</Badge>)}</div>}
                      </div>
                    ))}
                    {sen.length === 0 && <Empty label="No SEN accommodations" />}
                  </div>
                </SectionCard>
              </TabsContent>

              {/* ---------- MEDICATIONS ---------- */}
              <TabsContent value="medications" className="mt-4">
                <SectionCard icon={Pill} title="Medication register" tone="terracotta">
                  <TableList
                    rows={medications}
                    empty="No medications recorded"
                    render={(m:any) => (
                      <>
                        <td className="py-2 font-medium">{m.medicine_name}</td>
                        <td>{m.dose ?? "—"}</td>
                        <td className="text-muted-foreground">{m.frequency ?? "—"}</td>
                        <td className="text-muted-foreground">{m.route ?? "—"}</td>
                        <td className="text-xs">{m.indication ?? "—"}</td>
                        <td>{m.ongoing ? <Badge variant="destructive">ongoing</Badge> : <Badge variant="secondary">completed</Badge>}</td>
                      </>
                    )}
                    head={["Medicine", "Dose", "Frequency", "Route", "Indication", "Status"]}
                  />
                </SectionCard>
              </TabsContent>

              {/* ---------- IMMUNIZATIONS ---------- */}
              <TabsContent value="immunizations" className="mt-4">
                <SectionCard icon={Syringe} title="Immunization passport" tone="forest">
                  <div className="relative pl-8">
                    <div className="absolute left-3 top-1 bottom-1 w-0.5 bg-gradient-to-b from-[var(--forest,#0d7a5f)] via-[var(--ochre,#c9a84c)] to-transparent" />
                    {immunizations.map((im:any) => (
                      <div key={im.id} className="relative mb-3">
                        <div className="absolute -left-6 top-1 grid h-5 w-5 place-items-center rounded-full bg-[var(--forest,#0d7a5f)] ring-4 ring-background">
                          <Syringe className="h-2.5 w-2.5 text-white" />
                        </div>
                        <div className="rounded-lg border p-3">
                          <div className="flex items-center justify-between">
                            <div className="font-semibold">{im.vaccine} <span className="text-xs text-muted-foreground">· dose {im.dose_number}</span></div>
                            <Badge variant="outline">{im.administered_date ? new Date(im.administered_date).toLocaleDateString() : "—"}</Badge>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {im.administered_by && <>By {im.administered_by} · </>}
                            {im.batch_no && <>Batch {im.batch_no}</>}
                          </div>
                          {im.next_due_date && <div className="mt-1 text-xs text-[var(--ochre,#c9a84c)]">Next due · {new Date(im.next_due_date).toLocaleDateString()}</div>}
                        </div>
                      </div>
                    ))}
                    {immunizations.length === 0 && <Empty label="No immunization records" />}
                  </div>
                </SectionCard>
              </TabsContent>

              {/* ---------- CARE HISTORY ---------- */}
              <TabsContent value="history" className="mt-4 grid gap-3 lg:grid-cols-2">
                <SectionCard icon={Stethoscope} title="Care episodes" tone="terracotta">
                  <div className="space-y-2 max-h-[420px] overflow-auto">
                    {episodes.map((e:any) => (
                      <div key={e.id} className="rounded-lg border p-3">
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{e.reason ?? "—"}</div>
                          <div className="flex gap-1">
                            <Badge variant={e.severity==='critical'||e.severity==='high'?'destructive':'outline'}>{e.severity}</Badge>
                            <Badge variant={e.status==='open'?'destructive':'secondary'}>{e.status}</Badge>
                          </div>
                        </div>
                        <div className="text-[11px] text-muted-foreground">{shortDateTime(e.opened_at)} → {e.closed_at ? shortDateTime(e.closed_at) : 'ongoing'}</div>
                        {e.assessment && <div className="mt-1 text-xs">{e.assessment}</div>}
                        {Array.isArray(e.symptoms) && e.symptoms.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">{e.symptoms.map((s:string,i:number) => <Badge key={i} variant="outline" className="text-[9px]">{s}</Badge>)}</div>
                        )}
                      </div>
                    ))}
                    {episodes.length === 0 && <Empty label="No care episodes recorded" />}
                  </div>
                </SectionCard>

                <SectionCard icon={CalendarDays} title="Clinic visits & referrals" tone="ochre">
                  <div className="space-y-2 max-h-[420px] overflow-auto">
                    {visits.map((v:any) => (
                      <div key={v.id} className="rounded-lg border p-2.5 text-xs">
                        <div className="flex justify-between font-medium"><span>{v.reason ?? 'Visit'}</span><span className="text-[10px] text-muted-foreground">{v.visit_date ? new Date(v.visit_date).toLocaleDateString() : '—'}</span></div>
                        {v.nurse_note && <div className="text-muted-foreground mt-0.5">{v.nurse_note}</div>}
                      </div>
                    ))}
                    {referrals.map((r:any) => (
                      <div key={r.id} className="rounded-lg border-l-4 border-l-[var(--ochre,#c9a84c)] border p-2.5 text-xs">
                        <div className="flex justify-between font-medium"><span><ArrowUpRight className="inline h-3 w-3 mr-1" />Referral · {r.referral_type}</span><Badge variant={r.status==='open'?'destructive':'secondary'} className="text-[9px]">{r.status}</Badge></div>
                        <div className="text-muted-foreground">{r.destination_name} · {r.reason}</div>
                      </div>
                    ))}
                    {visits.length === 0 && referrals.length === 0 && <Empty label="No visit history" />}
                  </div>
                </SectionCard>

                <SectionCard icon={ScanLine} title="Screening results" tone="forest" className="lg:col-span-2">
                  <TableList
                    rows={screenings}
                    empty="No screening results"
                    render={(s:any) => (
                      <>
                        <td className="py-2 font-medium">{s.health_screening_campaigns?.campaign_name ?? '—'}</td>
                        <td>{s.result ?? '—'}</td>
                        <td className="text-muted-foreground">{s.outcome ?? '—'}</td>
                        <td className="text-[10px] text-muted-foreground">{s.screened_at ? new Date(s.screened_at).toLocaleDateString() : '—'}</td>
                      </>
                    )}
                    head={["Campaign", "Result", "Outcome", "Date"]}
                  />
                </SectionCard>
              </TabsContent>

              {/* ---------- WELLBEING ---------- */}
              <TabsContent value="wellbeing" className="mt-4 grid gap-3 lg:grid-cols-3">
                <SectionCard icon={Brain} title="Mood trend" tone="ink" className="lg:col-span-2">
                  <MoodSpark data={checkins} />
                </SectionCard>
                <SectionCard icon={HeartPulse} title="Snapshot" tone="ochre">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Check-ins</span><span className="font-semibold">{checkins.length}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Average mood</span><span className="font-semibold">{moodAvg ?? '—'}/10</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Flagged</span><span className="font-semibold text-[var(--terracotta,#A42022)]">{checkins.filter((c:any)=>c.flagged).length}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Avg sleep</span><span className="font-semibold">{checkins.length ? (checkins.reduce((a:number,c:any)=>a+(c.sleep_hours||0),0)/checkins.length).toFixed(1) : '—'}h</span></div>
                  </div>
                </SectionCard>
              </TabsContent>

              {/* ---------- DOCUMENTS ---------- */}
              <TabsContent value="documents" className="mt-4">
                <SectionCard icon={FileText} title="Medical documents" tone="ink">
                  <div className="grid gap-2 md:grid-cols-2">
                    {docs.map((d:any) => (
                      <a key={d.id} href={d.storage_url} target="_blank" rel="noreferrer"
                         className="flex items-center gap-3 rounded-lg border p-3 hover:bg-accent transition">
                        <div className="grid h-10 w-10 place-items-center rounded-md bg-[var(--ink,#1e1e1e)]/5 text-[var(--ink,#1e1e1e)]"><FileText className="h-5 w-5" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{d.title}</div>
                          <div className="text-[10px] text-muted-foreground">{d.document_type} · {shortDateTime(d.created_at)}</div>
                        </div>
                        {d.verified && <Badge variant="secondary" className="text-[9px]">verified</Badge>}
                      </a>
                    ))}
                    {docs.length === 0 && <Empty label="No documents uploaded" />}
                  </div>
                </SectionCard>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- helpers ---------- */

function HeroVital({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone?: "warn" | "good" }) {
  const bg = tone === "warn" ? "bg-[#c9a84c]/25 ring-[#c9a84c]/40" : "bg-white/10 ring-white/20";
  return (
    <div className={`rounded-xl px-3 py-2 backdrop-blur ring-1 ${bg}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/70"><Icon className="h-3 w-3" /> {label}</div>
      <div className="mt-0.5 font-serif text-lg leading-tight">{value}</div>
    </div>
  );
}

function bmiTone(bmi: number): "warn" | "good" | undefined {
  if (bmi < 15 || bmi > 28) return "warn";
  if (bmi >= 18.5 && bmi <= 24.9) return "good";
  return undefined;
}

function SectionCard({
  icon: Icon, title, tone = "forest", className, children,
}: { icon: any; title: string; tone?: "forest" | "terracotta" | "ochre" | "ink"; className?: string; children: React.ReactNode }) {
  const color =
    tone === "terracotta" ? "#A42022"
    : tone === "ochre" ? "#c9a84c"
    : tone === "ink" ? "#1e1e1e"
    : "#0d7a5f";
  return (
    <Card className={`overflow-hidden ${className ?? ""}`}>
      <div className="flex items-center gap-2 border-b bg-gradient-to-r px-4 py-2.5"
           style={{ borderColor: `${color}25`, background: `linear-gradient(90deg, ${color}18, transparent 60%)` }}>
        <div className="grid h-6 w-6 place-items-center rounded-md" style={{ background: `${color}22`, color }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="font-serif text-sm font-semibold tracking-wide" style={{ color }}>{title}</div>
      </div>
      <div className="p-3">{children}</div>
    </Card>
  );
}

function MiniStat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-lg border p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-serif text-2xl leading-none">{value}</div>
      {sub && <div className="mt-1 text-[10px] text-muted-foreground truncate">{sub}</div>}
    </div>
  );
}

function MiniList({ title, items }: { title: string; items?: string[] | null }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="mt-0.5">{Array.isArray(items) && items.length > 0 ? items.slice(0,4).map((it,i)=><div key={i}>· {it}</div>) : <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function TableList({ rows, head, render, empty }: { rows: any[]; head: string[]; render: (r: any) => React.ReactNode; empty: string }) {
  if (!rows.length) return <Empty label={empty} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">{head.map(h => <th key={h} className="pb-1 pr-2 font-medium">{h}</th>)}</tr></thead>
        <tbody className="divide-y">{rows.map(r => <tr key={r.id} className="[&>td]:pr-2">{render(r)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="grid place-items-center py-6 text-xs text-muted-foreground"><User className="h-6 w-6 opacity-30 mb-1" />{label}</div>;
}

function MoodSpark({ data }: { data: any[] }) {
  const sorted = [...data].reverse();
  if (!sorted.length) return <Empty label="No check-ins" />;
  const w = 480, h = 120, pad = 8;
  const xs = sorted.map((_, i) => pad + (i * (w - pad*2)) / Math.max(1, sorted.length - 1));
  const ys = sorted.map(d => pad + (h - pad*2) * (1 - Math.min(10, Math.max(0, d.mood_score || 0)) / 10));
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const area = `${path} L${xs[xs.length-1].toFixed(1)},${h-pad} L${xs[0].toFixed(1)},${h-pad} Z`;
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32">
        <defs>
          <linearGradient id="moodgrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0d7a5f" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#0d7a5f" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#moodgrad)" />
        <path d={path} fill="none" stroke="#0d7a5f" strokeWidth="2" />
        {sorted.map((d, i) => (
          <circle key={i} cx={xs[i]} cy={ys[i]} r={d.flagged ? 3.5 : 2} fill={d.flagged ? "#A42022" : "#0d7a5f"} />
        ))}
      </svg>
      <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
        <span>{sorted[0]?.submitted_at ? new Date(sorted[0].submitted_at).toLocaleDateString() : ""}</span>
        <span>{sorted[sorted.length-1]?.submitted_at ? new Date(sorted[sorted.length-1].submitted_at).toLocaleDateString() : ""}</span>
      </div>
    </div>
  );
}
